import React, {ReactElement, useRef, useState} from 'react';
import {Manager, Reference, Popper} from 'react-popper';
import contains from 'dom-helpers/contains';
import useTimeout from '@restart/hooks/useTimeout';

import './Tooltip.css';

type TooltipProps = {
    text?: string;
    contents?: any;
    placement?: 'right' | 'top';
    children: ReactElement<any, string> & ReactElement
};

// stolen / inspired by https://github.com/react-bootstrap/react-bootstrap/blob/master/src/OverlayTrigger.js
export const Tooltip: React.FunctionComponent<TooltipProps> = ({
                                                                   text = '',
                                                                   contents,
                                                                   placement = 'right',
                                                                   children
                                                               }) => {

    const [show, setShow] = useState<boolean>(false);
    const timeout = useTimeout();
    const hoverStateRef = useRef();
    const delay = {
        show: 0,
        hide: 300
    };

    const handleShow = () => {
        timeout.clear();

        // @ts-ignore
        hoverStateRef.current = 'show';

        if (!delay.show) {
            setShow(true);
            return;
        }

        timeout.set(() => {
            if (hoverStateRef.current === 'show') setShow(true);
        }, delay.show);
    };

    const handleHide = () => {
        timeout.clear();

        // @ts-ignore
        hoverStateRef.current = 'hide';

        if (!delay.hide) {
            setShow(false);
            return;
        }

        timeout.set(() => {
            if (hoverStateRef.current === 'hide') setShow(false);
        }, delay.hide);
    };

    // Simple implementation of mouseEnter and mouseLeave.
    // React's built version is broken: https://github.com/facebook/react/issues/4251
    // for cases when the trigger is disabled and mouseOut/Over can cause flicker
    // moving from one child element to another.
    const handleMouseOverOut = (handler: { (): void; (arg0: any): void; }, e: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>, relatedNative: string) => {
        const target = e.currentTarget;

        // @ts-ignore
        const related = e.relatedTarget || e.nativeEvent[relatedNative];

        if ((!related || related !== target) && !contains(target, related)) {
            handler(e);
        }
    };

    return (
        (text || contents) ?
            <Manager>
                <Reference>
                    {({ref}) => (
                        <span
                            ref={ref}
                            onMouseOut={(e) => {
                                handleMouseOverOut(handleHide, e, 'fromElement');
                            }}
                            onMouseOver={(e) => {
                                handleMouseOverOut(handleShow, e, 'toElement');
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                        >{children}</span>
                    )}
                </Reference>
                {show &&
                    <Popper placement={placement}>
                        {({ref, style, placement, arrowProps}) => (
                            <div ref={ref}
                                 style={{
                                     ...style,
                                     top: placement === 'top' ? '-15px' : 0
                                 }}
                                 data-placement={placement}
                                 className={`tooltip ${placement}`}
                            >
                                {contents ?
                                    <div className="tooltip-label">{contents}</div>
                                    :
                                    <div className="tooltip-label" dangerouslySetInnerHTML={{__html: text}}/>
                                }
                                <div className="tooltip-arrow" ref={arrowProps.ref} style={arrowProps.style}/>
                            </div>
                        )}
                    </Popper>
                }
            </Manager>
            :
            children
    );
};